import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchConfig } from "../hooks/useSearchConfig";
import { useNavigate } from "react-router-dom";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Building,
  ClipboardList,
  PlusIcon,
  Sparkles,
  Grid3X3,
  List,
} from "lucide-react";
import SearchVision360 from "../components/search-vision-360";
import { TicketSheet } from "@/features/vision-360/ticket";
import type { TicketRoom } from "@/features/vision-360/ticket";
import { Card, FilterValues, SearchResponse } from "../types";
import { VirtuosoGrid, Virtuoso } from "react-virtuoso";
import { apiVision360 } from "@/services/api-vision360";
import { debounce } from "@/utils/debounce";
import { isEqual } from "lodash";
import { LoadingRecommendations } from "@/components/ui/loading-recommendations";
import { Skeleton } from "@/components/ui/skeleton";
import { SelectionProvider } from "../contexts/SelectionContext";
import { useSelection } from "../contexts/SelectionContext";
import { Vision360Card } from "../components/Vision360Card";
import { Vision360ListItem } from "../components/Vision360ListItem";
import { Vision360Map, Vision360MapRef } from "../components/Vision360Map";
import { Vision360Drawer } from "../components/Vision360Drawer";
import { createToast } from "vercel-toast";
import {
  getCategoryIdFromPropertyType,
  getDefaultPropertyType,
} from "../constants/property-types";
import { getVipOfficePricePerPosition } from "../constants/vip-office-price-table";
import { useSearchVision } from "@/hooks/useSearchVision";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { Vision360ProposalModal } from "../components/modal-proposal-private-room";
import { Vision360ProposalLinksModal } from "../components/modal-proposal-links";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SortControls, sortCards, SortRule } from "../components/sort-controls";
import { isCardOutdated } from "../utils/card-status";
import {
  formatCityUf,
  formatCityUfFromParts,
  extractStateFromAddress,
  extractNeighborhoodFromLocation,
} from "../utils/format-city-uf";
import { privateOfficeService } from "@/services/private-office-service";

const GET_FROM_WOBA_BACKEND = true;
const SHOW_SELECTION_FOOTER = true;
const SHOW_LONG_LIST = false;
const DEFAULT_DISTANCE = 30;

/**
 * Applies the curation defaults to a persisted filter set. Both the initial
 * state and the config→state sync effect go through here: when the two applied
 * their own copy of these defaults, a partially-filled config (any missing
 * field) made the effect see a permanent difference and re-set state forever.
 */
function buildSearchFilters(filters?: FilterValues): FilterValues {
  return {
    query: filters?.query ?? "",
    propertyType: filters?.propertyType ?? getDefaultPropertyType(),
    isFurnished: filters?.isFurnished ?? false,
    budgetMin: filters?.budgetMin ?? "",
    budgetMax: filters?.budgetMax ?? "",
    sizeMin: filters?.sizeMin ?? "",
    sizeMax: filters?.sizeMax ?? "",
    positionsMin: filters?.positionsMin ?? "",
    positionsMax: filters?.positionsMax ?? "",
    minPrice: filters?.minPrice,
    maxPrice: filters?.maxPrice,
    minCapacity: filters?.minCapacity,
    maxCapacity: filters?.maxCapacity,
    privateOfficeStatus: filters?.privateOfficeStatus,
    amenitiesSlugs: filters?.amenitiesSlugs ?? [],
    amenitiesSlugsOptional: filters?.amenitiesSlugsOptional ?? [],
    distance: filters?.distance ?? DEFAULT_DISTANCE,
    unitCreditPrice: filters?.unitCreditPrice ?? 80,
    capacityWeight: filters?.capacityWeight,
    priceWeight: filters?.priceWeight,
    skipRecentUpdatesFilter: filters?.skipRecentUpdatesFilter ?? false,
    cityName: filters?.cityName,
    neighborhood: filters?.neighborhood,
    location: filters?.location,
  };
}

function SelectionFooter({
  totalCount,
  cards,
  onNext,
  isLoading,
  onCreateProposal,
  onOpenTicket,
  dealId,
}: {
  totalCount: number;
  cards: Card[];
  onNext: () => void;
  isLoading: boolean;
  onCreateProposal: () => void;
  onOpenTicket: () => void;
  dealId: string | null;
}) {
  const { selectionCount, selectAll, clearSelection, selectedIds } =
    useSelection();
  const allSelected = selectionCount === totalCount && totalCount > 0;

  const handleToggleAll = () => {
    if (allSelected) {
      clearSelection();
    } else {
      selectAll(cards.map((c) => c.id));
    }
  };

  return (
    <div className="fixed left-0 right-0 bottom-0 z-40 bg-white border-t py-4 px-6 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <button
          type="button"
          className="flex items-center space-x-2 px-3 py-1 transition-all duration-300 cursor-pointer"
          onClick={handleToggleAll}
          disabled={totalCount === 0}
        >
          <span className="text-sm text-gray-700">
            {allSelected ? "Desmarcar todos" : "Selecionar todos"}
          </span>
          <div
            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
              allSelected ? "bg-primary" : "bg-gray-200"
            }`}
            aria-hidden="true"
          >
            <span
              className={`${
                allSelected ? "translate-x-5" : "translate-x-1"
              } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
            />
          </div>
        </button>
        <span className="text-sm">
          ({selectionCount}/{totalCount}) selecionado
          {selectionCount !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="px-6 py-2 gap-1.5"
            onClick={onOpenTicket}
          >
            <ClipboardList className="w-4 h-4" />
            Abrir Ticket
          </Button>
          <Button
            variant="outline"
            className="px-6 py-2"
            onClick={() => {
              onCreateProposal();
              sendTrackDataServer({
                eventName: "VISION_CLICK_PRIVATEOFFICE_PROPOSAL_START",
                productType: "PO",
                data: {
                  private_office_ids: Array.from(selectedIds) ?? [],
                  deal_id: dealId ?? null,
                },
                url: location.pathname,
              });
            }}
            disabled={selectionCount === 0}
          >
            <PlusIcon className="w-4 mr-2" />
            Criar Long List
          </Button>
        </div>
        {SHOW_LONG_LIST && (
          <Button
            onClick={onNext}
            disabled={selectionCount === 0 || isLoading}
            className="bg-primary text-white px-6 py-2 rounded flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Criando Long List...
              </>
            ) : (
              "Avançar"
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

function CurationContent() {
  const { config, saveConfig } = useSearchConfig();
  const { clearSelection, selectedIds } = useSelection();
  const navigate = useNavigate();
  const [filteredCards, setFilteredCards] = useState<Card[]>([]);
  const [mapCards, setMapCards] = useState<Card[]>([]);
  const [drawerCard, setDrawerCard] = useState<Card | null>(null);
  const [isTicketSheetOpen, setIsTicketSheetOpen] = useState(false);
  const [searchFilters, setSearchFilters] = useState<FilterValues>(() =>
    buildSearchFilters(config?.filters),
  );
  const [_, setIsLoading] = useState(false);
  const [isLoadingLongList, setIsLoadingLongList] = useState(false);
  const mapRef = useRef<Vision360MapRef>(null);
  const [spacesWithRoomsIds, setSpacesWithRoomsIds] = useState<number[]>([]);
  const [spaceRoomCounts, setSpaceRoomCounts] = useState<
    Record<number, number>
  >({});
  const [isPainelFiltersOpen, setIsPainelFiltersOpen] =
    useState<boolean>(false);
  const [cardToCenter, setCardToCenter] = useState<string | null>(null);
  const [isProposalModalOpen, setIsProposalModalOpen] = useState(false);
  const [isProposalLinksModalOpen, setIsProposalLinksModalOpen] =
    useState(false);
  const [proposalData, setProposalData] = useState<{
    proposalLongUrl: string;
    proposalShortUrl: string;
    privateRoomsIds: string[];
  } | null>(null);
  const virtuosoRef = useRef(null);
  const virtuosoListRef = useRef(null);
  const SPACE_ID_NOT_SHOW = 1539;
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [sortRules, setSortRules] = useState<SortRule[]>([
    { field: "score", direction: "desc" },
  ]);

  const getArrayPictures = (
    image0: string | null,
    image1: string | null,
    image2: string | null,
    image3: string | null | undefined,
    image4: string | null | undefined,
    image5: string | null | undefined,
  ) => {
    const pictures: Array<string> = [];

    if (image0) {
      pictures.push(image0);
    }
    if (image1) {
      pictures.push(image1);
    }
    if (image2) {
      pictures.push(image2);
    }
    if (image3) {
      pictures.push(image3);
    }
    if (image4) {
      pictures.push(image4);
    }
    if (image5) {
      pictures.push(image5);
    }

    return pictures;
  };

  const parseFiltersToQuery = (filters: FilterValues): string => {
    const queryParts: string[] = [];
    if (filters.query) {
      queryParts.push(filters.query);
    }

    if (filters.isFurnished) {
      queryParts.push("mobiliado");
    }

    if (filters.budgetMin || filters.budgetMax) {
      const budgetRange = [];
      if (filters.budgetMin) {
        budgetRange.push(`R$ ${filters.budgetMin}`);
      }
      if (filters.budgetMax) {
        budgetRange.push(`R$ ${filters.budgetMax}`);
      }
      queryParts.push(`Preço ${budgetRange.join(" até ")}`);
    }

    if (filters.sizeMin || filters.sizeMax) {
      const sizeRange = [];
      if (filters.sizeMin) {
        sizeRange.push(`${filters.sizeMin}m²`);
      }
      if (filters.sizeMax) {
        sizeRange.push(`${filters.sizeMax}m²`);
      }
      queryParts.push(`tamanho ${sizeRange.join(" até ")}`);
    }

    if (filters.positionsMin || filters.positionsMax) {
      const positionsRange = [];
      if (filters.positionsMin) {
        positionsRange.push(`${filters.positionsMin}`);
      }
      if (filters.positionsMax) {
        positionsRange.push(`${filters.positionsMax}`);
      }
      queryParts.push(`${positionsRange.join(" a ")} posições`);
    }

    return queryParts.join(", ");
  };

  const {
    privateOffices,
    handleSearchVision,
    isLoading: isLoadingPrivateOffices,
  } = useSearchVision();

  const handleSearch = useCallback(
    async (filters: FilterValues) => {
      if (!filters.searchType) {
        return;
      }

      const searchSelectedValue = filters.location;
      const uf =
        searchSelectedValue && searchSelectedValue.match(/- ([A-Z]{2}),/)
          ? searchSelectedValue?.match(/- ([A-Z]{2}),/)
          : null;

      const demandParams = {
        ...filters,
        distance: Number(filters.distance),
        minCapacity:
          Number(filters.minCapacity) === 0
            ? null
            : Number(filters.minCapacity),
        maxCapacity:
          Number(filters.maxCapacity) === 0
            ? null
            : Number(filters.maxCapacity),
        minPrice:
          Number(filters.minPrice) === 0 ? null : Number(filters.minPrice),
        maxPrice:
          Number(filters.maxPrice) === 0 ? null : Number(filters.maxPrice),
        unitCreditPrice:
          Number(filters.unitCreditPrice) === 0
            ? null
            : Number(filters.unitCreditPrice),
        amenitiesSlugs: filters.amenitiesSlugs,
        amenitiesSlugsOptional: filters.amenitiesSlugsOptional,
        maxCapacityWeight: Number(filters.capacityWeight),
        minCapacityWeight: Number(filters.capacityWeight),
        minPriceWeight: Number(filters.priceWeight),
        maxPriceWeight: Number(filters.priceWeight),
        privateOfficeStatus: filters.privateOfficeStatus?.map(Number) ?? [],
        uf: uf?.[1] ?? undefined,
        skipRecentUpdatesFilter: filters.skipRecentUpdatesFilter,
        categoryIds: getCategoryIdFromPropertyType(filters.propertyType[0]),
      };

      await Promise.all([
        handleSearchVision({
          term: searchSelectedValue,
          demands: {
            [searchSelectedValue || filters.searchType]: demandParams,
          },
          dealUrl: config?.dealId?.toString() ?? undefined,
        }),
        privateOfficeService
          .searchPrivateOffices(
            {
              demands: {
                [searchSelectedValue || filters.searchType]: {
                  ...demandParams,
                  skipRecentUpdatesFilter: true,
                },
              },
            },
            "map",
          )
          .then((response) => {
            const mapCards: Card[] = (response.results || []).map(
              (item: any) =>
                ({
                  id: item.private_room_id,
                  title: item.label,
                  address:
                    `${item.space_details?.address || ""}, ${item.space_details?.neighborhood || ""}`.trim(),
                  coords: {
                    lat: Number(item.space_details?.latitude || 0),
                    lng: Number(item.space_details?.longitude || 0),
                  },
                  status: item.status,
                  days_since_update: item.days_since_update,
                  threshold_days: item.threshold_days ?? undefined,
                  is_outdated: item.is_outdated ?? undefined,
                }) as Card,
            );
            setMapCards(mapCards);
            setSpacesWithRoomsIds(response.spaces_returned_ids || []);
          })
          .catch((error) => {
            console.error("Error loading map data:", error);
            setMapCards([]);
          }),
      ]);
    },
    [handleSearchVision, config?.dealId],
  );

  useEffect(() => {
    setFilteredCards(
      (
        privateOffices?.results?.filter(
          (item) => item.space_id !== SPACE_ID_NOT_SHOW,
        ) ?? []
      ).map((item) => ({
        id: item.details?.id?.toString() || item.private_room_id || "",
        search_id: item.search_id,
        title: item.details?.label || item.label || "",
        address: item.space_details?.address || "",
        neighborhood: item.space_details?.neighborhood || undefined,
        cityname: item.space_details?.cityname || undefined,
        space: {
          name: item.space_details?.name || "",
          id:
            item.space_details?.id?.toString() ||
            item.space_id?.toString() ||
            "",
        },
        capacity: item.capacity,
        ...(item.origin === "integration" &&
        item.source === "vip-office" &&
        !item.monthly_price
          ? (() => {
              const pricePerPosition = getVipOfficePricePerPosition(
                item.label || "",
                item.space_details?.name || "",
                item.space_details?.neighborhood || "",
                item.space_details?.cityname || "",
              );
              const capacity = item.capacity || 0;
              const monthlyPrice =
                pricePerPosition > 0 && capacity > 0
                  ? pricePerPosition * capacity
                  : 0;
              const unitPrice = searchFilters.unitCreditPrice ?? 80;
              return {
                monthly_price: monthlyPrice,
                credits:
                  monthlyPrice > 0
                    ? Math.ceil(monthlyPrice / unitPrice)
                    : item.monthly_credit_price,
              };
            })()
          : {
              credits: item.monthly_credit_price,
              monthly_price: item.monthly_price,
            }),
        status: item.status ?? item.details?.status ?? 0,
        size: item.size ?? item.details?.size ?? undefined,
        updated_at:
          item.details?.updated_at || item.details?.data_updated_at || "",
        images: getArrayPictures(
          item.details?.picture_url,
          item.details?.picture_url1,
          item.details?.picture_url2,
          item.details?.picture_url3,
          item.details?.picture_url4,
          item.details?.picture_url5,
        ),
        original_url: item.dpp || "",
        coords: {
          lat: item.space_details?.latitude
            ? Number(item.space_details.latitude)
            : 0,
          lng: item.space_details?.longitude
            ? Number(item.space_details.longitude)
            : 0,
        },
        match_percentage: item.match_percent,
        score: item.detailed_score_analysis?.map((scoreItem) => ({
          criterion: scoreItem.criterion,
          requested: scoreItem.requested,
          offered: scoreItem.offered,
          matched: scoreItem.matched,
        })) as any,
        score_details: item?.score_details,
        source: item.source || "internal",
        origin: item.origin || "internal",
        external_url: item.external_url,
        type: "internal",
        space_id:
          item.space_id?.toString() ||
          item.space_details?.id?.toString() ||
          undefined,
        private_room_id: item.private_room_id,
        monthly_credit_price: item.monthly_credit_price,
        days_since_update: item.days_since_update,
        threshold_days: item.threshold_days ?? undefined,
        is_outdated: item.is_outdated ?? undefined,
        distance_km: item.distance_km ?? undefined,
        amenities: item.amenities || item.details?.amenities || undefined,
        attributes: item.attributes || undefined,
        details: item.details
          ? {
              id: item.details.id,
              search_id: item.search_id ?? undefined,
              space_id:
                item.details.space_id?.toString() ||
                item.space_id?.toString() ||
                undefined,
              label: item.details.label,
              external_url: item.details.external_url || undefined,
              size: item.details.size ?? undefined,
              capacity: item.details.capacity,
              monthly_price: item.details.monthly_price,
              yearly_price: item.details.yearly_price ?? undefined,
              amenities: item.details.amenities || undefined,
              extra_services: item.details.extra_services || undefined,
              shared_amenities: item.details.shared_amenities || undefined,
              shared_extra_services:
                item.details.shared_extra_services || undefined,
              advantages: item.details.advantages || undefined,
              picture_url: item.details.picture_url || undefined,
              picture_url1: item.details.picture_url1 || undefined,
              picture_url2: item.details.picture_url2 || undefined,
              picture_url3: item.details.picture_url3 || undefined,
              picture_url4: item.details.picture_url4 || undefined,
              picture_url5: item.details.picture_url5 || undefined,
              notes: item.details.notes || undefined,
              status: item.details.status ?? undefined,
              category: item.details.category ?? undefined,
              created_at: item.details.created_at || undefined,
              updated_at:
                item.details.updated_at ||
                item.details.data_updated_at ||
                undefined,
              deleted_at: item.details.deleted_at || undefined,
              created_by_user_id: item.details.created_by_user_id || undefined,
              private_office_amenities_attributes: item.details
                .grouped_amenities
                ? item.details.grouped_amenities.flatMap(
                    (group) =>
                      group.amenities?.map((amenity) => ({
                        amenity: {
                          name: amenity.name,
                          category: group.name || "",
                          slug:
                            amenity.name?.toLowerCase().replace(/\s+/g, "-") ||
                            "",
                        },
                        private_office_attribute: amenity.attributes || {},
                      })) || [],
                  )
                : undefined,
              status_updated_at: item.details.data_updated_at || undefined,
            }
          : undefined,
        space_details: item.space_details
          ? {
              id: item.space_details.id,
              acknowledge_date:
                item.space_details.acknowledge_date || undefined,
              name: item.space_details.name,
              enabled: item.space_details.enabled ?? undefined,
              type: item.space_details.type ?? undefined,
              latitude: item.space_details.latitude || undefined,
              longitude: item.space_details.longitude || undefined,
              cityname: item.space_details.cityname || undefined,
              neighborhood: item.space_details.neighborhood || undefined,
              address: item.space_details.address || undefined,
              amenities: item.space_details.amenities || undefined,
              vanish_name: item.space_details.vanish_name || undefined,
            }
          : undefined,
      })),
    );
  }, [privateOffices, searchFilters.unitCreditPrice]);

  useEffect(() => {
    const counts: Record<number, number> = {};
    filteredCards.forEach((card) => {
      const spaceId = card.space_id || card.space_details?.id;
      if (spaceId) {
        const id = parseInt(String(spaceId), 10);
        counts[id] = (counts[id] || 0) + 1;
      }
    });
    setSpaceRoomCounts(counts);
  }, [filteredCards]);

  useEffect(() => {
    if (cardToCenter && filteredCards.length > 0) {
      const index = filteredCards.findIndex((card) => card.id === cardToCenter);
      if (index !== -1) {
        setTimeout(() => {
          if (viewMode === "grid" && virtuosoRef.current) {
            // @ts-expect-error - VirtuosoGrid ref type
            virtuosoRef.current.scrollToIndex({
              index,
              align: "center",
              behavior: "smooth",
            });
          } else if (viewMode === "list" && virtuosoListRef.current) {
            // @ts-expect-error - Virtuoso ref type
            virtuosoListRef.current.scrollToIndex({
              index,
              align: "center",
              behavior: "smooth",
            });
          }
        }, 100);
      }
      setCardToCenter(null);
    }
  }, [cardToCenter, filteredCards, viewMode]);

  const debouncedSearch = useCallback(
    debounce((filters: FilterValues) => {
      if (
        !filters.searchType ||
        (filters.searchType === "location" && !filters.location) ||
        (filters.searchType === "space" && !filters.spaceId) ||
        (filters.searchType === "privateOfficeIds" &&
          (!filters.privateOfficeIds ||
            filters.privateOfficeIds.length === 0)) ||
        (filters.searchType === "neighborhoods" &&
          (!filters.neighborhood || filters.neighborhood.length === 0))
      ) {
        createToast(
          "É necessário informar uma localização, sala, espaço ou bairro para continuar!",
          {
            type: "error",
            timeout: 5000,
          },
        );
        return;
      }

      if (GET_FROM_WOBA_BACKEND) {
        handleSearch(filters);
        return;
      }

      const query = parseFiltersToQuery(filters);
      setIsLoading(true);

      const hasQuery = query !== "";
      const hasFilters =
        filters.minPrice !== undefined ||
        filters.maxPrice !== undefined ||
        filters.minCapacity !== undefined ||
        filters.maxCapacity !== undefined ||
        (filters.privateOfficeStatus &&
          filters.privateOfficeStatus.length > 0) ||
        (filters.amenitiesSlugs && filters.amenitiesSlugs.length > 0) ||
        (filters.amenitiesSlugsOptional &&
          filters.amenitiesSlugsOptional.length > 0) ||
        (filters.distance !== undefined &&
          filters.distance !== DEFAULT_DISTANCE) ||
        (filters.unitCreditPrice !== undefined &&
          filters.unitCreditPrice !== 80) ||
        filters.capacityWeight !== undefined ||
        filters.priceWeight !== undefined;

      if (!hasQuery && !hasFilters) {
        setFilteredCards([]);
        setIsLoading(false);
        return;
      }

      const payload: any = {
        query,
        generate_justifications: true,
      };

      if (filters.minPrice !== undefined) {
        payload.min_price = filters.minPrice;
      }
      if (filters.maxPrice !== undefined) {
        payload.max_price = filters.maxPrice;
      }
      if (filters.priceWeight !== undefined) {
        payload.price_weight = filters.priceWeight;
      }
      if (filters.minCapacity !== undefined) {
        payload.min_capacity = filters.minCapacity;
      }
      if (filters.maxCapacity !== undefined) {
        payload.max_capacity = filters.maxCapacity;
      }
      if (filters.capacityWeight !== undefined) {
        payload.capacity_weight = filters.capacityWeight;
      }
      if (
        filters.privateOfficeStatus &&
        filters.privateOfficeStatus.length > 0
      ) {
        payload.private_office_status = filters.privateOfficeStatus;
      }
      if (filters.amenitiesSlugs && filters.amenitiesSlugs.length > 0) {
        payload.amenities_slugs = filters.amenitiesSlugs;
      }
      if (
        filters.amenitiesSlugsOptional &&
        filters.amenitiesSlugsOptional.length > 0
      ) {
        payload.amenities_slugs_optional = filters.amenitiesSlugsOptional;
      }
      if (filters.distance !== undefined) {
        payload.distance = filters.distance;
      }
      if (filters.unitCreditPrice !== undefined) {
        payload.unit_credit_price = filters.unitCreditPrice;
      }

      apiVision360
        .post<SearchResponse>("/search", payload)
        .then((res) => res.data)
        .then((data: SearchResponse) => {
          const cards: Card[] = data.results.map((result) => ({
            id: result.building.id,
            search_id: Number(result.building.id),
            type: result.building.type,
            title: result.building.name,
            space: {
              name: result.building.space.name,
              id: result.building.space.id,
            },
            capacity: result.capacity,
            monthly_price: result.monthly_price,
            credits: result.credits,
            status: Number(result.building.status),
            updated_at: result.building.updated_at,
            address: result.building.location.address,
            images: result.building.images,
            features: result.building.features,
            pricing: result.building.pricing,
            description: result.building.description,
            verified: result.building.source === "bts_list",
            source: result.building.source,
            original_url: result.building.original_url,
            whatsapp_number: result.building.whatsapp_number,
            phone: result.building.phone,
            coords: {
              lat: result.building.location.latitude,
              lng: result.building.location.longitude,
            },
            recommendation_level: result?.recommendation_level,
            recommendation_ai: result?.recommendation_ai,
          }));
          setFilteredCards(cards);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 500),
    [handleSearch],
  );

  useEffect(() => {
    if (!config?.filters) return;
    // Compare the *derived* next state against the current one — comparing the
    // raw config against defaulted state never converges when a field is absent.
    const next = buildSearchFilters(config.filters);
    setSearchFilters((prev) => (isEqual(prev, next) ? prev : next));
  }, [config?.filters]);

  useEffect(() => {
    if (config?.filters && config.filters.searchType) {
      const hasValidSearchCriteria =
        (config.filters.searchType === "location" && config.filters.location) ||
        (config.filters.searchType === "space" && config.filters.spaceId) ||
        (config.filters.searchType === "privateOfficeIds" &&
          config.filters.privateOfficeIds &&
          config.filters.privateOfficeIds.length > 0) ||
        (config.filters.searchType === "neighborhoods" &&
          config.filters.neighborhood &&
          config.filters.neighborhood.length > 0);

      if (hasValidSearchCriteria && config.filters) {
        setTimeout(() => {
          debouncedSearch(config.filters!);
        }, 100);
      }
    }
  }, [config?.filters, config?.dealId, debouncedSearch]);

  const previousSearchCriteriaRef = useRef<{
    searchType?: string;
    location?: string;
    spaceId?: number;
    privateOfficeIds?: string[] | number[] | null;
    neighborhood?: string[];
  }>({});

  useEffect(() => {
    if (config?.filters) {
      const currentCriteria = {
        searchType: config.filters.searchType,
        location: config.filters.location,
        spaceId: config.filters.spaceId,
        privateOfficeIds: config.filters.privateOfficeIds,
        neighborhood: config.filters.neighborhood,
      };

      const previousCriteria = previousSearchCriteriaRef.current;

      const hasChanged =
        previousCriteria.searchType !== currentCriteria.searchType ||
        previousCriteria.location !== currentCriteria.location ||
        previousCriteria.spaceId !== currentCriteria.spaceId ||
        JSON.stringify(previousCriteria.privateOfficeIds) !==
          JSON.stringify(currentCriteria.privateOfficeIds) ||
        JSON.stringify(previousCriteria.neighborhood) !==
          JSON.stringify(currentCriteria.neighborhood);

      if (
        hasChanged &&
        Object.keys(previousCriteria).length > 0 &&
        config.selectedCards &&
        config.selectedCards.length > 0
      ) {
        clearSelection();
      }

      previousSearchCriteriaRef.current = currentCriteria;
    }
  }, [
    config?.filters?.searchType,
    config?.filters?.location,
    config?.filters?.spaceId,
    config?.filters?.privateOfficeIds,
    config?.filters?.neighborhood,
    config?.selectedCards,
    clearSelection,
  ]);

  const handleNext = useCallback(async () => {
    setIsLoadingLongList(true);
    if (config) {
      saveConfig({
        currentStep: "long-list",
      });

      try {
        const response = await apiVision360.post("/api/v1/longlists", {
          user_id: config?.userId,
          client_name: config?.dealName || config?.dealId,
          deal_id: config?.dealId,
          description: "string",
          building_ids: config.selectedCards,
          data: {
            user_name: config?.userName,
            user_email: config?.userEmail,
            created_at: config?.createdAt,
            updated_at: config?.updatedAt,
          },
        });

        if (response.status !== 200) {
          console.error("Error creating long list", response.data);
          setIsLoadingLongList(false);
          return;
        }

        const data = response.data;
        saveConfig({ longListId: data.id });

        navigate(`${DEFAULT_ROUTES.VISION_360_BETA_LONG_LIST}?l_id=${data.id}`);
        setIsLoadingLongList(false);
      } catch (error) {
        console.error("Error creating long list", error);
        createToast("Ocorreu um erro ao criar a Long List", {
          type: "error",
          timeout: 3000,
        });
        setIsLoadingLongList(false);
      }
    }
  }, [config, navigate, saveConfig]);

  const handleBack = () => {
    if (config) {
      const defaultFilters = {
        query: "",
        propertyType: getDefaultPropertyType(),
        isFurnished: false,
        budgetMin: "" as number | "",
        budgetMax: "" as number | "",
        sizeMin: "" as number | "",
        sizeMax: "" as number | "",
        positionsMin: "" as number | "",
        positionsMax: "" as number | "",
        minPrice: undefined,
        maxPrice: undefined,
        minCapacity: undefined,
        maxCapacity: undefined,
        privateOfficeStatus: ["0"],
        amenitiesSlugs: [],
        amenitiesSlugsOptional: [],
        distance: DEFAULT_DISTANCE,
        unitCreditPrice: 80,
        capacityWeight: undefined,
        priceWeight: undefined,
        searchType: undefined,
        location: undefined,
        latitude: undefined,
        longitude: undefined,
        spaceId: undefined,
        privateOfficeIds: undefined,
        cityName: undefined,
        neighborhood: undefined,
      };

      saveConfig({
        currentStep: "setup",
        filters: defaultFilters,
      });
      navigate(DEFAULT_ROUTES.VISION_360_BETA_SETUP);
    }
  };

  const handleMarkerClick = useCallback((card: Card) => {
    setCardToCenter(card.id);
  }, []);

  const handleCenterOnMap = useCallback((card: Card) => {
    if (mapRef.current) {
      mapRef.current.centerOnCard(card);
    }
  }, []);

  const handleMapCardClick = useCallback(
    (optimizedCard: Card) => {
      const fullCard = filteredCards.find((c) => c.id === optimizedCard.id);
      if (fullCard) {
        setDrawerCard(fullCard);
      } else {
        console.warn(
          `Full card not found for ID ${optimizedCard.id}, using optimized data`,
        );
        setDrawerCard(optimizedCard);
      }
    },
    [filteredCards],
  );

  const sortedCards = useMemo(() => {
    return sortCards(filteredCards, sortRules);
  }, [filteredCards, sortRules]);

  // Filter out outdated assets for list/grid when filter is not active
  const cardsForList = useMemo(() => {
    if (searchFilters.skipRecentUpdatesFilter) {
      return sortedCards; // Show all cards
    }
    // Remove outdated assets when filter is unchecked
    return sortedCards.filter((card) => !isCardOutdated(card));
  }, [sortedCards, searchFilters.skipRecentUpdatesFilter]);

  const memoizedCards = useMemo(() => cardsForList, [cardsForList]);

  const selectedRooms = useMemo(
    () =>
      filteredCards
        .filter((c) => selectedIds.has(c.id))
        .map(
          (c): TicketRoom => ({
            roomId: c.id,
            spaceId: c.space?.id ?? c.id,
            roomName: c.title,
            spaceName: c.space?.name ?? "",
            address: c.address,
            capacity: c.capacity,
            sizeM2: c.size ?? null,
            neighborhood: c.neighborhood,
            cityName: c.cityname,
          }),
        ),
    [filteredCards, selectedIds],
  );

  // Last-resort city for the ticket: the rooms being ticketed are themselves
  // reliable evidence of the city, which matters when the search was too broad,
  // too specific, or (as with a foreign address) yields no "Cidade - UF" at all.
  // Built from the room's own city field plus the state at the end of its
  // address — never by parsing the city out of the address, which has no fixed
  // shape and would pass a neighborhood off as the city.
  const selectedRoomsCity = useMemo(() => {
    for (const room of selectedRooms) {
      const city = formatCityUfFromParts(
        room.cityName,
        extractStateFromAddress(room.address),
      );
      if (city) return city;
    }
    return null;
  }, [selectedRooms]);

  const handleCreateProposal = useCallback(() => {
    setIsProposalModalOpen(true);
  }, []);

  const handleProposalSuccess = useCallback(
    (data: {
      proposalLongUrl: string;
      proposalShortUrl: string;
      privateRoomsIds: string[];
    }) => {
      setProposalData(data);
      setIsProposalModalOpen(false);
      setIsProposalLinksModalOpen(true);

      sendTrackDataServer({
        eventName: "VISION_CREATE_PROPOSAL_FINISHED",
        data: {
          deal_id: config?.dealId?.toString() ?? null,
          private_room_ids: data.privateRoomsIds,
        },
        url: location.pathname,
        productType: "PO",
      });
    },
    [config?.dealId],
  );

  const handleCloseProposalModal = useCallback(() => {
    setIsProposalModalOpen(false);
  }, []);

  const handleCloseProposalLinksModal = useCallback(() => {
    setIsProposalLinksModalOpen(false);
    setProposalData(null);
  }, []);

  return (
    <>
      <div className="relative h-[calc(100vh-4rem)] flex flex-row">
        <div className="w-full md:w-2/3 flex flex-col h-full p-4">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-2xl font-bold">Curadoria</h1>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
                <Sparkles className="h-4 w-4" />
                <span className="hidden md:inline">Powered by AI</span>
              </div>
              {filteredCards.length > 0 && (
                <SortControls
                  onSortChange={setSortRules}
                  defaultSort={sortRules}
                />
              )}
              <div className="flex items-center gap-1 border rounded-lg p-1">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={viewMode === "grid" ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setViewMode("grid")}
                        className="h-8 w-8 p-0"
                      >
                        <Grid3X3 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Ver em cards</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={viewMode === "list" ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setViewMode("list")}
                        className="h-8 w-8 p-0"
                      >
                        <List className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Ver em lista</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
          <div className="mb-2">
            <SearchVision360
              defaultFilters={{
                query: "",
                propertyType:
                  config?.filters?.propertyType ?? getDefaultPropertyType(),
                isFurnished: false,
                budgetMin: "",
                budgetMax: "",
                sizeMin: "",
                sizeMax: "",
                positionsMin: "",
                positionsMax: "",
                minPrice: config?.filters?.minPrice ?? undefined,
                maxPrice: config?.filters?.maxPrice ?? undefined,
                minCapacity: config?.filters?.minCapacity ?? undefined,
                maxCapacity: config?.filters?.maxCapacity ?? undefined,
                privateOfficeStatus: config?.filters?.privateOfficeStatus ?? [
                  "0",
                ],
                amenitiesSlugs: config?.filters?.amenitiesSlugs ?? [],
                amenitiesSlugsOptional:
                  config?.filters?.amenitiesSlugsOptional ?? [],
                distance: config?.filters?.distance ?? DEFAULT_DISTANCE,
                unitCreditPrice: config?.filters?.unitCreditPrice ?? 80,
                capacityWeight: config?.filters?.capacityWeight ?? undefined,
                priceWeight: config?.filters?.priceWeight ?? undefined,
                searchType: config?.filters?.searchType ?? undefined,
                location: config?.filters?.location ?? undefined,
                spaceId: config?.filters?.spaceId ?? undefined,
                privateOfficeIds:
                  config?.filters?.privateOfficeIds ?? undefined,
                cityName: config?.filters?.cityName ?? undefined,
                neighborhood: config?.filters?.neighborhood ?? undefined,
                skipRecentUpdatesFilter:
                  config?.filters?.skipRecentUpdatesFilter ?? false,
              }}
              onSearch={(filters) => {
                debouncedSearch(filters);
                if (config) {
                  saveConfig({ filters });
                }
              }}
              onPainelFiltersOpenChange={setIsPainelFiltersOpen}
              dealId={config?.dealId?.toString() ?? null}
            />
          </div>
          <div
            className={`flex-1 min-h-0 relative ${SHOW_SELECTION_FOOTER ? "mb-16" : ""}`}
          >
            {isLoadingPrivateOffices && <LoadingRecommendations />}
            {isLoadingPrivateOffices ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="border rounded-lg bg-white shadow-sm overflow-hidden flex flex-col h-full"
                  >
                    <div className="relative h-40 w-full flex-shrink-0">
                      <Skeleton className="w-full h-full" />
                    </div>
                    <div className="p-4 flex flex-col flex-1 min-h-0">
                      <Skeleton className="h-6 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2 mb-4" />
                      <Skeleton className="h-8 w-1/3 mb-2" />
                      <Skeleton className="h-4 w-2/3 mb-2" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredCards.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <Building className="h-16 w-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Nenhum resultado encontrado
                </h3>
                <p className="text-gray-500">
                  Tente ajustar seus filtros de busca para encontrar mais
                  ativos.
                </p>
              </div>
            ) : viewMode === "grid" ? (
              <VirtuosoGrid
                ref={virtuosoRef}
                data={memoizedCards}
                totalCount={memoizedCards.length}
                itemContent={(_, card) => (
                  <Vision360Card
                    key={card.id}
                    card={card}
                    onOpenDrawer={setDrawerCard}
                    showSelectButton={SHOW_SELECTION_FOOTER}
                    onCenterOnMap={handleCenterOnMap}
                    showCenterOnMapButton={true}
                  />
                )}
                overscan={400}
                listClassName="grid grid-cols-1 md:grid-cols-2 gap-4"
                itemClassName="w-full h-full"
              />
            ) : (
              <div className="flex flex-col h-full border border-gray-200 rounded-lg bg-white overflow-hidden">
                <div className="flex-1 min-h-0">
                  <TooltipProvider>
                    <Virtuoso
                      ref={virtuosoListRef}
                      data={memoizedCards}
                      totalCount={memoizedCards.length}
                      itemContent={(_, card) => (
                        <Vision360ListItem
                          key={card.id}
                          card={card}
                          onOpenDrawer={setDrawerCard}
                          showSelectButton={SHOW_SELECTION_FOOTER}
                          onCenterOnMap={handleCenterOnMap}
                          showCenterOnMapButton={true}
                        />
                      )}
                      overscan={200}
                    />
                  </TooltipProvider>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="hidden md:block md:w-1/3 relative z-0 h-full">
          <Vision360Map
            ref={mapRef}
            cards={mapCards}
            spacesWithRoomsIds={spacesWithRoomsIds}
            spaceRoomCounts={spaceRoomCounts}
            onOpenDrawer={handleMapCardClick}
            customBottomPosition={SHOW_SELECTION_FOOTER ? 70 : 0}
            onMarkerClick={handleMarkerClick}
            centerCoords={
              config?.filters?.latitude && config?.filters?.longitude
                ? {
                    lat: parseFloat(config.filters.latitude),
                    lng: parseFloat(config.filters.longitude),
                  }
                : null
            }
            includeRecentUpdates={
              searchFilters.skipRecentUpdatesFilter ?? false
            }
            locationFilters={
              config?.filters?.latitude && config?.filters?.longitude
                ? {
                    latitude: parseFloat(config.filters.latitude),
                    longitude: parseFloat(config.filters.longitude),
                    distance: config.filters.distance,
                  }
                : config?.filters?.distance
                  ? {
                      distance: config.filters.distance,
                    }
                  : undefined
            }
          />
        </div>
        {filteredCards.length > 0 &&
          !isPainelFiltersOpen &&
          SHOW_SELECTION_FOOTER && (
            <SelectionFooter
              totalCount={filteredCards.length}
              cards={filteredCards}
              onNext={handleNext}
              isLoading={isLoadingLongList}
              onCreateProposal={handleCreateProposal}
              onOpenTicket={() => setIsTicketSheetOpen(true)}
              dealId={config?.dealId?.toString() ?? null}
            />
          )}
        <Vision360Drawer
          open={!!drawerCard}
          card={drawerCard}
          onClose={() => setDrawerCard(null)}
          showSelectButton={SHOW_SELECTION_FOOTER}
          dealId={config?.dealId?.toString() ?? null}
        />
        <TicketSheet
          open={isTicketSheetOpen}
          onClose={() => setIsTicketSheetOpen(false)}
          selectedRooms={selectedRooms}
          prefillCity={
            formatCityUf(searchFilters.cityName) ??
            formatCityUf(searchFilters.location) ??
            formatCityUf(config?.filters?.cityName) ??
            formatCityUf(config?.filters?.location) ??
            selectedRoomsCity ??
            ""
          }
          prefillNeighborhoods={
            searchFilters.neighborhood ??
            config?.filters?.neighborhood ??
            [
              extractNeighborhoodFromLocation(
                searchFilters.location ?? config?.filters?.location,
              ),
            ].filter((n): n is string => !!n)
          }
          prefillPositions={searchFilters.minCapacity ?? null}
          dealId={config?.dealId}
          dealName={config?.dealName}
        />
        <Vision360ProposalModal
          open={isProposalModalOpen}
          origin="vision-backoffice"
          onClose={handleCloseProposalModal}
          onSuccess={handleProposalSuccess}
          allCards={filteredCards}
          dealId={config?.dealId?.toString()}
          filters={config?.filters}
        />
        <Vision360ProposalLinksModal
          open={isProposalLinksModalOpen}
          onClose={handleCloseProposalLinksModal}
          proposalLongUrl={proposalData?.proposalLongUrl || ""}
          proposalShortUrl={proposalData?.proposalShortUrl || ""}
          privateRoomsIds={proposalData?.privateRoomsIds || []}
          dealId={config?.dealId?.toString()}
        />
      </div>
    </>
  );
}

export default function CurationLayout() {
  const { config, saveConfig } = useSearchConfig();

  const handleSelectionChange = useCallback(
    (selectedIds: Set<string>) => {
      if (config) {
        saveConfig({ selectedCards: Array.from(selectedIds) });
      }
    },
    [config, saveConfig],
  );

  return (
    <SelectionProvider
      initialSelection={new Set(config?.selectedCards || [])}
      onSelectionChange={handleSelectionChange}
    >
      <CurationContent />
    </SelectionProvider>
  );
}
